import { slugify } from "./helpcenter.js";

/**
 * Beperkte Markdown voor kennisbankartikelen (docs/HELPCENTER.md §5): koppen, vet, cursief, code, lijsten,
 * links, afbeeldingen en een Tip-blok (`:::tip` … `:::`). Alle HTML in de bron wordt ge-escaped; er komt
 * alleen HTML uit die deze functie zelf maakt. Gedeeld door de app (Hulp, voorbeeld in de editor), de server
 * (export) en de helpsite.
 */

const esc = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

/**
 * Alleen http(s), mailto, absolute paden, ankers en relatieve paden; nooit `javascript:` of `data:`.
 * Stuurtekens en spaties weigeren we helemaal: browsers halen ze weg (`\u0001javascript:` wordt `javascript:`).
 * Een backslash ook: `/\\host` is voor browsers hetzelfde als `//host`.
 */
export function safeUrl(url: string): string | null {
  const u = url.trim();
  // eslint-disable-next-line no-control-regex -- juist stuurtekens weigeren
  if (!u || /[\u0000-\u0020\u007f-\u009f\\]/.test(u)) return null;
  if (/^(https?:\/\/|mailto:)/i.test(u)) return u;
  if (u.includes(":") && !/^[^:]*[/?#]/.test(u)) return null;
  if (/^\/\//.test(u)) return null;
  return u;
}

function inline(src: string): string {
  // Code eerst apart houden, zodat er binnen code geen opmaak gebeurt.
  const codes: string[] = [];
  let s = src.replace(/`([^`]+)`/g, (_, c: string) => `\uE000${codes.push(c) - 1}\uE000`);
  s = esc(s);
  s = s.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (m, alt: string, url: string) => {
    const u = safeUrl(url.replace(/&amp;/g, "&"));
    return u ? `<img src="${esc(u)}" alt="${alt}" loading="lazy">` : alt;
  });
  s = s.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (m, text: string, url: string) => {
    const u = safeUrl(url.replace(/&amp;/g, "&"));
    if (!u) return text;
    const ext = /^https?:\/\//i.test(u) ? ' rel="noopener"' : "";
    return `<a href="${esc(u)}"${ext}>${text}</a>`;
  });
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/(^|[^\w*])\*([^*\s][^*]*)\*/g, "$1<em>$2</em>");
  s = s.replace(/(^|[^\w])_([^_\s][^_]*)_(?!\w)/g, "$1<em>$2</em>");
  return s.replace(/\uE000(\d+)\uE000/g, (_, i: string) => `<code>${esc(codes[Number(i)]!)}</code>`);
}

export function renderHelpMarkdown(md: string): string {
  const lines = md.replace(/\r\n?/g, "\n").split("\n");
  const out: string[] = [];
  let para: string[] = [];
  let list: { tag: "ul" | "ol"; items: string[] } | null = null;
  const flushPara = () => {
    if (para.length) out.push(`<p>${para.map(inline).join("<br>")}</p>`);
    para = [];
  };
  const flushList = () => {
    if (list) out.push(`<${list.tag}>${list.items.map((i) => `<li>${inline(i)}</li>`).join("")}</${list.tag}>`);
    list = null;
  };
  const flush = () => {
    flushPara();
    flushList();
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const t = line.trim();
    if (/^:::\s*tip\b/i.test(t)) {
      flush();
      const inner: string[] = [];
      for (i++; i < lines.length && lines[i]!.trim() !== ":::"; i++) inner.push(lines[i]!);
      out.push(`<aside class="tip"><p class="tip-label">Tip</p>${renderHelpMarkdown(inner.join("\n"))}</aside>`);
      continue;
    }
    const h = /^(#{1,3})\s+(.+)$/.exec(t);
    if (h) {
      flush();
      const level = Math.max(2, h[1]!.length);
      const text = h[2]!.replace(/\s+#+$/, "");
      out.push(`<h${level} id="${slugify(text)}">${inline(text)}</h${level}>`);
      continue;
    }
    const ul = /^[-*]\s+(.+)$/.exec(t);
    const ol = /^\d+[.)]\s+(.+)$/.exec(t);
    if (ul || ol) {
      flushPara();
      const tag = ul ? "ul" : "ol";
      if (list && list.tag !== tag) flushList();
      list ??= { tag, items: [] };
      list.items.push((ul ?? ol)![1]!);
      continue;
    }
    if (!t) {
      flush();
      continue;
    }
    const img = /^!\[([^\]]*)\]\(([^)\s]+)\)$/.exec(t);
    if (img) {
      flush();
      const caption = img[1] ? `<figcaption>${esc(img[1])}</figcaption>` : "";
      out.push(`<figure>${inline(t)}${caption}</figure>`);
      continue;
    }
    // Vervolgregel van een lijstitem (ingesprongen).
    if (list && /^\s{2,}\S/.test(line)) {
      list.items[list.items.length - 1] += ` ${t}`;
      continue;
    }
    flushList();
    para.push(t);
  }
  flush();
  return out.join("\n");
}

/** Platte tekst (voor de samenvatting en de meta-description). */
export function helpMarkdownToText(md: string, max = 200): string {
  const text = md
    .replace(/^:::.*$/gm, "")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^[-*]\s+|^\d+[.)]\s+/gm, "")
    .replace(/[*_`]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > max ? `${text.slice(0, max - 1).replace(/\s+\S*$/, "")}…` : text;
}
